import {lazy, Suspense, useEffect, useState} from 'react'
import type {HeadersArgs, LoaderFunctionArgs, MetaFunction} from 'react-router'
import {useLoaderData} from 'react-router'

import {mergeMeta} from '@core/lib/mergeMeta'
import {json} from '@core/lib/json'
import {getProductId} from '@core/products/registry'
import {logger} from '@core/utils/logger.server'
import {readMine} from '@core/wall/mine.server'
import {GATE_TRIGGER_WALL, type Identity, type MineResponse} from '@core/wall/contract'
import {Wall} from '@core/wall/ui/Wall'
import {SignInLayer} from '@core/auth-ui/SignInLayer/SignInLayer'
import {TosConsentCard} from '@core/consent/TosConsentCard'
import type {ConsentCardProps} from '@core/consent/useConsentGate'

import {readMineAllowance} from '@product/ovid/wall/allowance.server'
import {readWallQueryParam, withWallQuery} from '@core/wall/tsQuery'
import {readBasis, readOvidWall, searchRobotsHeaders} from '@product/ovid/wall/wallRead.server'
import {visitorPanelData, visitorSeamProof} from '@product/ovid/wall/visitorPanel.server'
import {VisitorSignInLayer} from '@product/ovid/wall/VisitorPanel'
import {VisitorSeam} from '@product/ovid/wall/VisitorSeam'
import {arrivalLine, MUSE_CHOICE_PARAM, readMuseChoice} from '@product/ovid/wall/musePanel'
import {chooseMuseIfNone} from '@product/ovid/services/museChoice.server'
import {OvidWallHeader} from '@product/ovid/wall/header'
import {OVID_SIGN_IN_CARD} from '@product/ovid/wall/signInCard'
import {ovidTheme} from '@product/ovid/wall/theme'
import {HomeDoorstep} from '@core/wall/home/HomeDoorstep'
import {homeMeta} from '@core/wall/home/doorstep'
import {ovidHome} from '@product/ovid/wall/home'
import {VeiledFooter} from '@product/ovid/components/VeiledFooter/VeiledFooter'
import {resolveOwnerMuse} from '@product/ovid/packs/packRun.server'
import consentStyles from '@product/ovid/packs/TosConsentCard.module.scss'

// The muse intake opens over the wall from the strip's + frame; its chunk (the intake hook, the photo screening
// client) is loaded when it opens, not with a visitor's wall.
const IdentityIntake = lazy(() => import('@product/ovid/wall/IdentityIntake').then((m) => ({default: m.IdentityIntake})))

/**
 * Ovid's home — the wall (`thinking/plans/2026_09_17_ovid_veiled_wall.md`, step 6: "Ovid's home
 * is the wall"). Replaces the earlier packs-driven marketing home entirely (the previous hero
 * wall / catalog / manifesto / FAQ tree — history in `thinking/threads/ovid.md`); `/packs` and the
 * individual pack pages are untouched.
 *
 * ONE wall, both states (plan `2026_09_17_ovid_one_wall.md` § Decided 1) — the shared `<Wall>`
 * (`@core/wall/ui`), the same component and the same cells Toile mounts, with this house's
 * differences expressed as `ovidTheme`'s own `visitor` policy. Anonymous: the finite veiled
 * composition on the wire (`AnonWallResponse`), same bytes for every visitor and for Googlebot — no
 * pack slug, no title, no chip, no old-host URL (`wallRead.server.ts`'s own header doc). Signed in:
 * the catalog — a sign-in that tapped a cell re-arms the same tile through the wall's own `basis`
 * token (`ovidTheme.visitor.returnTo`, read on mount by `Wall.tsx`'s return-token handling), rather
 * than the earlier `Unveil` cross-fade this route used to render first.
 */
export const meta: MetaFunction = ({matches, location}) =>
    mergeMeta(matches, [
        ...homeMeta(ovidHome),
        // A search URL is a view of the wall, never a page of its own: canonical `/` (`homeMeta`) and out of the index.
        ...(readWallQueryParam(new URLSearchParams(location.search)) ? [{name: 'robots', content: 'noindex'}] : []),
    ])

export async function loader({request}: LoaderFunctionArgs) {
    let read: Awaited<ReturnType<typeof readOvidWall>>
    try {
        read = await readOvidWall(request)
    } catch (err) {
        logger.error('[wall][ovid] home SSR loader failed', {
            error: err,
        })
        // Degrade to an empty anonymous page rather than losing the document — the same posture
        // Toile's own `_layout._index.tsx` loader takes on its own read failure.
        return json(
            {anon: true as const, page: {tiles: [], composition_count: 0}, mine: null, profile: '', basis: null, panel: visitorPanelData(), proof: null, arrival: null},
            {headers: {'Cache-Control': 'no-store'}},
        )
    }

    if (read.anon) {
        // The proof body under the seam's panel: a read that fails leaves the panel alone, never loses the wall.
        const proof = await visitorSeamProof().catch((err) => {
            logger.error('[wall][ovid] the visitor seam proof could not be read', {error: err})
            return null
        })
        // `private, no-store`, same as the signed-in branch below (finding 6,
        // `2026_09_17_wall_sharing_review.md`): one URL answers two bodies by session, so a shared
        // cache in front of it — CloudFront's own default is not to store this response, but a
        // browser can — must never serve one visitor's fetch back to another's session.
        return json(
            {
                anon: true as const, page: read.page, mine: null, profile: read.profile,
                basis: readBasis(new URL(request.url).searchParams), panel: visitorPanelData(), proof, arrival: null,
            },
            {headers: {'Cache-Control': 'private, no-store', ...searchRobotsHeaders(request)}},
        )
    }

    // The muse a visitor chose before the card comes back on the address: it becomes the account's muse when the account
    // has none (never over a chosen one), before the identity below is read, so the first paint already has the verb.
    const url = new URL(request.url)
    const choice = readMuseChoice(url.search)
    let arrivalMuse: string | null = null
    if (choice) {
        try {
            arrivalMuse = await chooseMuseIfNone(read.userId, choice)
        } catch (err) {
            logger.error('[wall][ovid] the muse chosen before sign-in could not be applied', {userId: read.userId, error: err})
        }
    }

    let mineCore: Awaited<ReturnType<typeof readMine>> | null = null
    try {
        mineCore = await readMine({product: getProductId(), owner: {kind: 'user', userId: read.userId}, q: ''})
    } catch (err) {
        logger.error('[mine][ovid] home SSR readMine failed', {
            userId: read.userId,
            error: err,
        })
    }

    // Resolved the same way `GET /mine` resolves it (`routes/mine.ts`) — the wall's very first
    // paint carries the real identity rather than a `'none'` placeholder the first poll would
    // otherwise have to correct a moment later.
    let identity: Identity = {kind: 'muse', slug: null, name: null}
    try {
        const {museSlug, muse} = await resolveOwnerMuse(read.userId)
        identity = {kind: 'muse', slug: museSlug, name: muse?.name ?? null, thumb: muse?.thumb ?? null}
    } catch (err) {
        logger.error('[wall][ovid] home SSR identity resolution failed — defaulting to museless', {
            userId: read.userId,
            error: err,
        })
    }

    // Back from the bar's plan checkout (`/checkout/plan` returns `plan=success&session_id=`): the paid session is
    // recorded before the allowance is read, so the first paint already stands under the plan, webhook or not.
    const paidSession = url.searchParams.get('plan') === 'success' ? url.searchParams.get('session_id') : null
    if (paidSession) await (await import('@product/ovid/services/purchase.server')).recordOvidPlanFromSession(paidSession, read.userId)
    const allowance = await readMineAllowance(read.userId)

    const mine: MineResponse | null = mineCore
        ? {
              done: mineCore.done, pending: mineCore.pending, pinned: mineCore.pinned, eta_ms: mineCore.eta_ms, identity,
              ...(allowance ? {allowance} : {}),
          }
        : null

    return json(
        {anon: false as const, page: read.page, mine, profile: read.profile, facetOffer: read.facetOffer, arrival: arrivalMuse ? arrivalLine(arrivalMuse) : null},
        {headers: {'Cache-Control': 'private, no-store', ...searchRobotsHeaders(request)}},
    )
}

export function headers({loaderHeaders}: HeadersArgs) {
    const cacheControl = loaderHeaders.get('Cache-Control')
    const robots = loaderHeaders.get('X-Robots-Tag')
    return {...(cacheControl ? {'Cache-Control': cacheControl} : {}), ...(robots ? {'X-Robots-Tag': robots} : {})}
}

// Ovid's own consent copy — the same strings the pack workbench's `CtaSlot.tsx` already ships
// (`plans/2026_09_01_tos_gate.md` §8.2), reused rather than re-authored so the wall's terms gate
// reads identically to the one a signed-in visitor already knows from a pack page.
const WALL_CONSENT_COPY = {
    title: 'One quiet thing',
    sub: 'Before her frames.',
    attestation: 'I am 18+ and agree to the',
    termsLabel: 'Terms',
    conjunction: 'and',
    policyLabel: 'Acceptable Use',
}

/** Every prop the two states share — the wall is one component and one mount; only `anonPage` vs
 *  `initialWallPage`, `signedIn`, and the ending below tell the two calls apart. */
function consentCard({checked, onTick, focusToken}: ConsentCardProps) {
    return (
        <TosConsentCard
            styles={consentStyles}
            checked={checked}
            onTick={onTick}
            focusToken={focusToken}
            termsHref="/terms"
            privacyHref="/terms#acceptable-use"
            copy={WALL_CONSENT_COPY}
        />
    )
}

function signInLayer({redirectTo, trigger, onClose}: {redirectTo: string; trigger: string; onClose: () => void}) {
    return <SignInLayer redirectTo={redirectTo} trigger={trigger} onClose={onClose} card={OVID_SIGN_IN_CARD} />
}

export default function OvidHome() {
    const data = useLoaderData<typeof loader>()
    // The muse chosen on any of the visitor's panels (the seam, the wall's end, the layer): one choice for all of them.
    // The first muse is chosen to begin with, so the sign-in always brings a muse back and Render works on arrival.
    const [chosen, setChosen] = useState<string | null>(() => (data.anon ? data.panel.muses[0]?.slug ?? null : null))

    // The choice came back on the address and the loader has applied it: the address does not keep it.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        if (!params.has(MUSE_CHOICE_PARAM)) return
        params.delete(MUSE_CHOICE_PARAM)
        const search = params.toString()
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${search ? `?${search}` : ''}`)
    }, [])

    if (data.anon) {
        const returnTo = (q: string) => withWallQuery(ovidTheme.visitor!.returnTo(data.basis), q)
        return (
            <>
                <Wall
                    theme={ovidTheme}
                    anonPage={data.page}
                    profile={data.profile}
                    initialMine={null}
                    signedIn={false}
                    renderHeader={(search, signIn) => <OvidWallHeader search={search} onSignIn={signIn} />}
                    renderSignIn={({redirectTo, trigger, onClose}) => (
                        <VisitorSignInLayer
                            data={data.panel} chosen={chosen} onChoose={setChosen} redirectTo={redirectTo} trigger={trigger}
                            inputId="wall-layer-email" onClose={onClose}
                        />
                    )}
                    renderConsentCard={consentCard}
                    footer={(q, openSignIn) => (
                        <VisitorSeam
                            data={data.panel} chosen={chosen} onChoose={setChosen} redirectTo={returnTo(q)}
                            trigger={GATE_TRIGGER_WALL} inputId="wall-seam-email" proof={data.proof} onSignIn={openSignIn}
                        />
                    )}
                />
                <HomeDoorstep house={ovidHome} anon gallery={data.page.tiles} />
                <VeiledFooter />
            </>
        )
    }

    return (
        <Wall
            theme={ovidTheme}
            initialWallPage={data.page}
            profile={data.profile}
            facetOffer={data.facetOffer}
            initialMine={data.mine}
            signedIn
            arrivalNotice={data.arrival}
            renderHeader={(search, signIn) => <OvidWallHeader search={search} onSignIn={signIn} />}
            renderSignIn={signInLayer}
            renderConsentCard={consentCard}
            renderIdentityIntake={({onClose, reopenPath}) => (
                <Suspense fallback={null}>
                    {/* No armed tile to come back to: the wall reopens the intake on `/?intake=1`. */}
                    <IdentityIntake onClose={onClose} signedIn signupRedirectTo={reopenPath ?? '/?intake=1'} />
                </Suspense>
            )}
        />
    )
}
